<script setup lang="ts">
import type * as pf from '@amirada/lib-shared'
import { NodeType } from '@amirada/lib-shared'
import { ELogicGroup, ESubTab, ETab, PARAM } from '@amirada/lib-wilo-shared'
import type { IDetailProps } from '@base/composables/detail'
import { useDetail } from '@base/composables/detail'
import { useBreadCrumb } from '@base/composables/breadCrumb'
import {
  ut_formatSchemaNumber,
  ut_getObjectByPath,
  ut_getText,
  ut_getValuesByPath, ut_isEmbedded,
  ut_isPrint,
  ut_isScreenSize,
  ut_navigateNewRoute,
} from '@base/utils/utils'
import { EIconSize, EItemState, def_newGraphicSubTabs } from '~/utils/constants'
import type { AccordionItem, TabsItem } from '@nuxt/ui'
import {
  ut_wilo_isAllowlistedUser,
  ut_wilo_isEmpCo,
  ut_wilo_isFlattenedTab,
  ut_wilo_ItemState,
  ut_wilo_ItemStateColor,
  ut_wilo_isPriceList,
  ut_wilo_salesValue,
  ut_wilo_seriesDescriptionParts,
  ut_wilo_seriesDesignParts,
  ut_wilo_shoppingLinks,
  ut_wilo_showSubTab,
  ut_wilo_showTab,
} from '~/utils/utils'
import type * as tpWilo from '~/utils/types'
import { useAppIndexStore } from '@base/stores/indexStore'
import { ESchemaItemAvailability, ESchemaItemCondition, EScreenSize } from '@base/utils/constants'
import type { BreadcrumbItem } from '#ui/types'
import type * as tp from '@base/utils/types'

export interface IDetailPropsCustom extends IDetailProps {
  isHistoric: boolean
  isMotors: boolean
  isInvisibleOem: boolean
  isServices: boolean
  isProducts: boolean
  isSeries: boolean
  isInternal: boolean
  countryLanguage: string
  realCountry: string
  /*
   * Rendered inside the digital product pass rather than as the page the reader navigated to.
   *
   * Named after the CALLER, not after one effect, because more than one thing hangs off it:
   *
   *  - the settings bar. Country, language and frequency live in the url, so changing one there
   *    would navigate away and take the shown product with it — and the bar would look like it
   *    belonged to the pass while it actually changes the whole catalogue.
   *  - the cart icons. The pass answers what a serial number is, and putting that answer on a
   *    price list or into a comparison is a different errand; the product's own page is where it
   *    belongs.
   *
   * Optional, so every existing caller keeps everything without being touched.
   */
  isDpp?: boolean
}

const p = defineProps<IDetailPropsCustom>()
const cp = useDetail(p)
const cpBreadCrumb = useBreadCrumb()

const indexStore = useAppIndexStore()
const showModalPdf: Ref<boolean> = ref(false)
const metaInitialized: Ref<boolean> = ref(false)
const selectedAccordion: Ref<string> = ref('')
let singleTab: string = ''
let wholeSalerIFrame: boolean = false

const isOld = computed((): boolean => {
  return p.isHistoric || itemState.value.state === EItemState.st_expired
})

// TEMPORARY (24.09.2026): the promotional subline and the advantages list may not comply with the EU
// EmpCo directive — hidden in those markets until the texts are cleared. Remove with the two v-if
// conditions below once they are. See ai_memory/project_empco_markets_20260924.md.
const isEmpCo = computed((): boolean => ut_wilo_isEmpCo(p.realCountry))

function detailSetup(): boolean {
  if (cp.detailSetup.value && cp.detailData.value) {
    // Update tab on selected accordion
    selectedAccordion.value = cp.indexStore.state.paramData.tab + (cp.indexStore.state.paramData.tab2 ? '|' + cp.indexStore.state.paramData.tab2 : '')

    if (!metaInitialized.value) { setMeta() }

    return true
  }
  else {
    return false
  }
}

function setMeta(): void {
  if (!cp.detailData.value) { return }

  metaInitialized.value = true

  // single Tab by ArticleNo or SeriesID, redirect to real URL
  if (cp.indexStore.state.paramData[PARAM.isIFrame]) {
    const type: string = cp.indexStore.state.paramData[PARAM.isIFrame].split('|')[1] ?? ''
    const key: string = type.split(':')[0] || ''
    switch (key) {
      case 'wholesaler':
        wholeSalerIFrame = true
        break
      case 'tab':
        singleTab = type.split(':')[1] || ''
        break
    }
  }

  // Head elememnts
  let title: string = cp.detailData.value.desc
  let h2: string
  let description: string = ''
  let image: string[]

  if (cp.detailData.value.nodeType === NodeType.nt_Configuration) {
    h2 = ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultTexts, '8ae2819c89cdcef00189e3827dff2b12'].join('/'))
    // COMPAT: new configs nest advantages under the range_sub_advantages subtab — try the nested path
    // first, fall back to the old flat path. Remove the fallback once every config is migrated.
    description = ut_getValuesByPath(cp.detailData.value, [ETab.rangeAdvantages, ESubTab.rangeSubAdvantages, ELogicGroup.rangeAdvantagesTexts, '8ae2819c6314804e01632148675d542a'].join('/'))
      || ut_getValuesByPath(cp.detailData.value, [ETab.rangeAdvantages, ELogicGroup.rangeAdvantagesTexts, '8ae2819c6314804e01632148675d542a'].join('/'))
    image = ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultGraphics, 'graphic_data_product_photo_beauty'].join('/'), true, -1, '|').split('|').filter(Boolean)
  }
  else {
    h2 = ut_getValuesByPath(cp.detailData.value, [ETab.productDefault, ELogicGroup.productAdditional, '8ae2819c9993428b01999ff325396f83'].join('/'))
    if (!h2) {
      h2 = ut_getValuesByPath(cp.detailData.value, [ETab.productDefault, ELogicGroup.productAdditional, '89e1809c9b9905b8019ba35f76283ec5'].join('/'))
    }
    const tenderTextTab: pf.TPfNode | undefined = ut_getObjectByPath(cp.detailData.value, [ETab.productTendertext].join('/'))
    if (tenderTextTab && tenderTextTab.nodes && tenderTextTab.nodes[0] && tenderTextTab.nodes[0].props && tenderTextTab.nodes[0].props[0]) {
      const prop: pf.TPfNode = tenderTextTab.nodes[0].props[0]
      if (prop.values && prop.values[0]) {
        const text: string = prop.values[0].desc ?? ''
        const split: string[] = text.split('<P>')
        description = (split[1] ?? '') + (split[2] ? '. ' + split[2] : '')
      }
    }
    image = ut_getValuesByPath(cp.detailData.value, [ETab.productDefault, ELogicGroup.productPhotoGraphics, 'graphic_data_product_photo_select'].join('/'), true, -1, '|').split('|').filter(Boolean)
  }
  if (h2) { title += ' | ' + h2 }
  description = description.replace(/<[^>]+>/g, '')

  if (image[0]) {
    if (!image[0].includes('empty_1')) {
      image = image.map(p => p.replace('_1.', '_3.'))
    }
  }

  // Set SEO data
  const headElements: tp.IHeadElements = {
    title,
    description,
  }
  indexStore.setHeadElements(headElements)

  // Set OG data
  const openGraphElements: tp.IOpenGraphElements = {
    title,
    description,
    imageUrl: image[0] || '',
  }
  indexStore.setOpenGraphElements(openGraphElements)

  // Schema for products
  if (cp.detailData.value.nodeType !== NodeType.nt_Configuration) {
    const schemas: tp.ISchemas = {
      schemaProduct: {
        name: cp.detailData.value.desc,
        description,
        image,
      },
    }

    // Sales data
    const salesData: pf.TPfNode | undefined = ut_getObjectByPath(cp.detailData.value, [ETab.productTechnical, ESubTab.productSalesdata].join('/'))
    if (salesData && salesData.nodes && salesData.nodes[0] && salesData.nodes[0].nodes && schemas.schemaProduct) {
      const comps: pf.TPfNode[] = salesData.nodes[0].nodes
      let comp: pf.TPfNode | undefined
      comp = comps.find(c => c.cod === 'Prices')
      if (comp) {
        const value: string = getValueForProp(comp, '8ae2819c6f7f32a8016f80ced2b12cba')
        if (value) {
          try {
            const json: tpWilo.IPriceObj[] = JSON.parse(value || '{}')
            if (json) {
              const today: number = new Date().getTime()
              for (let i = 0; i < json.length; i++) {
                if (Date.parse(json[i]?.begin || '') <= today && Date.parse(json[i]?.until || '') > today - 86400000) {
                  const priceObj: tpWilo.IPriceObj | undefined = json[i]
                  if (priceObj && priceObj.price > 1) {
                    // The offer is created HERE and nowhere before. An `offers: {}` waiting to be
                    // filled still reaches the json-ld as `{"@type": "Offer"}` when no price turns
                    // up — `getJsonLD` only asks whether the field exists, and an empty object is
                    // truthy. An Offer without price and priceCurrency is an ERROR in Google's rich
                    // results, and it would hit every product whose price data is missing, expired
                    // or below the threshold below.
                    schemas.schemaProduct.offers = {
                      price: ut_formatSchemaNumber(priceObj.price.toString()),
                      priceCurrency: priceObj.currency,
                      priceValidUntil: priceObj.until.split('T')[0],
                      itemCondition: ESchemaItemCondition.sch_condition_new,
                      availability: ESchemaItemAvailability.sch_availability_storeonly,
                      url: indexStore.getAbsolutePath,
                    }
                  }
                  break
                }
              }
            }
          }
          catch (err) { /* nothing */ }
        }
      }
      comp = comps.find(c => c.cod === 'Product_data')
      if (comp) {
        // A brand only when it has a name — otherwise the json-ld claims a brand and names none.
        const brandName: string = getValueForProp(comp, '8ae2819c5c9a3ffe015c9c640ac750db')
        if (brandName) {
          schemas.schemaProduct.brand = { name: brandName }
        }
        // Same rule as above, one level flatter: getValueForProp returns '' for a property that is
        // not there, and an empty string is a malformed value to a consumer, not a missing one.
        const sku: string = getValueForProp(comp, '8ae2819d5b31c2ca015b3dc4ba4f1612')
        if (sku) { schemas.schemaProduct.sku = sku }
        const gtin: string = getValueForProp(comp, '8ae2819c62a85d280162b50af0b4159e')
        if (gtin) { schemas.schemaProduct.gtin = gtin }
        const color: string = getValueForProp(comp, '8ae2819c63a4b2730163a6262d506dc2')
        if (color) { schemas.schemaProduct.color = color }
        const releaseDate: string = getValueForProp(comp, '8ae2819c65e57b0d0165fbac128e1ef6')
        if (releaseDate) { schemas.schemaProduct.releaseDate = releaseDate }
      }
      comp = comps.find(c => c.cod === 'Dimension,_weight')
      if (comp) {
        // Each measure only if it actually came back: getDimensionsObj returns an empty object
        // when the property is missing, and a QuantitativeValue without a value says nothing.
        const depth: tp.ISchemaQuantitativeValue = getDimensionsObj(comp, '8ae2819c69246d18016924a5e41c50c2', 'MMT')
        if (depth.value) { schemas.schemaProduct.depth = depth }
        const height: tp.ISchemaQuantitativeValue = getDimensionsObj(comp, '8ae2819c69246d18016924a486c1043d', 'MMT')
        if (height.value) { schemas.schemaProduct.height = height }
        const width: tp.ISchemaQuantitativeValue = getDimensionsObj(comp, '8ae2819c69246d18016924a65b8d14d4', 'MMT')
        if (width.value) { schemas.schemaProduct.width = width }
        const weight: tp.ISchemaQuantitativeValue = getDimensionsObj(comp, '8ae2819d5b31c2ca015b3dc4bcd371e2', 'KGM')
        if (weight.value) { schemas.schemaProduct.weight = weight }
      }
    }
    indexStore.setSchemas(schemas)
  }
}

function getValueForProp(comp: pf.TPfNode, name: string): string {
  let res: string = ''
  const prop = comp.props?.find(p => p.nodeName === name)
  if (prop && prop.values && prop.values[0]) {
    res = prop.values[0].desc
  }
  return res
}

function getDimensionsObj(comp: pf.TPfNode, name: string, unitCode: string): { value?: string, unitText?: string, unitCode?: string } {
  const res: { value?: string, unitText?: string, unitCode?: string } = {}
  const prop = comp.props?.find(p => p.nodeName === name)
  if (prop && prop.values && prop.values[0]) {
    const value = prop.values[0].desc
    const numberCur: string[] = value.split(String.fromCharCode(160))
    res.value = ut_formatSchemaNumber(numberCur[0] || '')
    res.unitText = numberCur[1]
    res.unitCode = unitCode
  }
  return res
}

const accProps = computed((): Record<string, number> => {
  const res: Record<string, number> = {}
  if (cp.detailData.value?.meta?.acc) {
    for (const header of cp.detailData.value.meta.acc.header) {
      res[header.cod] = header.nodeID
    }
  }
  return res
})

const seriesItem = computed((): BreadcrumbItem | undefined => {
  if (!p.isSeries) {
    return cpBreadCrumb.breadCrumbData.value[cpBreadCrumb.breadCrumbData.value.length - 2]
  }
  else {
    return undefined
  }
})

// Filter tabs to show
const detailTabs = computed((): TabsItem[] => {
  const tabs: TabsItem[] = cp.detailTabs.value
  for (let i = 0; i < tabs.length; i++) {
    const tabName: string = (tabs[i]?.value || '').toString()
    let showTab: boolean = ut_wilo_showTab(tabName)
    if (showTab) {
      switch (tabName) {
        case ETab.productBimCad:
          showTab = bimDataExists()
          break
        case ETab.productTechnical:
          showTab = !p.isInvisibleOem
          break
        // TEMPORARY: the motors tab is not live for embedded hosts yet - standalone or internal only
        // (Christoph, 22.09.2026), the same gating as the new graphic subtabs below. It is out of the
        // PDF as well (ModalPDF.useTab), so print never asks for it.
        case ETab.productMotors:
          showTab = !ut_isEmbedded() || p.isInternal
          break
      }
    }
    if (!showTab) {
      tabs.splice(i, 1)
      i--
    }
  }
  return tabs
})

const detailTabs2 = computed((): { [value: string]: TabsItem[] } => {
  if (!cp.detailData.value) { return {} }
  const tabs: { [value: string]: TabsItem[] } = cp.detailTabs2.value
  // TEMP (until final acceptance): the new range_accessory / range_services subtabs are only shown to
  // internal + allowlisted users — same gating as the cart management feature. Non-test users keep
  // seeing everything as before (range_advantages then flattens to just its advantages content below).
  const showNewSubTabs: boolean = (p.isInternal && ut_wilo_isAllowlistedUser()) || ut_isPrint() || !ut_isEmbedded()
  // TEMPORARY: the new graphic subtabs (def_newGraphicSubTabs) are not live yet — standalone or
  // internal only. A print run shows what ModalPDF offered, whatever host renders it.
  const showNewGraphicSubTabs: boolean = !ut_isEmbedded() || p.isInternal || ut_isPrint()
  for (const key of Object.keys(tabs)) {
    const tab: TabsItem[] | undefined = tabs[key]
    if (tab) {
      for (let i = 0; i < tab.length; i++) {
        const tabName: string = (tab[i]?.value || '').toString()
        let showTab: boolean = ut_wilo_showSubTab(tabName, cp.detailData.value, indexStore.state.paramData.rcco, p.isHistoric)
        if ((tabName === ESubTab.rangeAccessory || tabName === ESubTab.rangeServices) && !showNewSubTabs) {
          showTab = false
        }
        if (def_newGraphicSubTabs.includes(tabName) && !showNewGraphicSubTabs) {
          showTab = false
        }
        if (!showTab) {
          tab.splice(i, 1)
          i--
        }
      }
      // The flattening of range_advantages used to happen HERE, by deleting the subtab level. It
      // does not any more: the level stays and only the accordion ENTRY below collapses (label of
      // the tab, value of the subtab) — see ut_wilo_isFlattenedTab for why the value must keep it.
    }
  }
  return tabs
})

/*
 * The tabs a print run asks for, as a LIST.
 *
 * ⚑ `param2` arrives RAW from the query (indexStore.ts) — it is url text, not an array. Asking it
 * `.includes(value)` was therefore a SUBSTRING search, and every tab whose name sits inside another
 * entry matched: a series whose advantages tab carries no subtab (older configs, and still the case
 * on some data stands) has the flat entry `range_advantages`, which is contained in
 * `range_advantages|range_accessory` — so price lists printed advantages nobody asked for.
 *
 * Parsed here rather than in the store: paramData holds what the url said, and only this page knows
 * that this particular parameter is a list. A value that is not a list stays one entry, which keeps
 * hand-written urls (`param2=range_productlist`) working.
 */
const printTabs = computed((): string[] => {
  const raw: unknown = indexStore.state.paramData[PARAM.paramTabs]
  if (Array.isArray(raw)) { return raw.map(entry => String(entry)) }
  const text: string = String(raw || '')
  if (!text) { return [] }
  try {
    const parsed: unknown = JSON.parse(text)
    return Array.isArray(parsed) ? parsed.map(entry => String(entry)) : [text]
  }
  catch {
    return [text]
  }
})

/*
 * Does this tab get a subtab bar of its own on the desktop?
 *
 * ⚑ Not simply "does it have subtabs": the flattened one has exactly one, carrying the same label as
 * the tab above it, and a bar with a single entry repeating its own heading is what the flattening
 * exists to avoid. The content then comes from the tab node, and DetailContent resolves the subtab
 * itself (advantagesNode) — which is how it worked before the subtab level stayed in place.
 *
 * ⭐ Same question as in accordionItems, asked through the same function, so the desktop and the
 * accordion cannot disagree about it.
 */
function showsSubTabs(tabValue: string): boolean {
  const subTabs: TabsItem[] = detailTabs2.value[tabValue] || []
  if (!subTabs.length) { return false }
  return !ut_wilo_isFlattenedTab(tabValue, subTabs.map(sub => (sub.value || '').toString()))
}

// Whether a tab block is printed at all — the print run asks for a fixed set, an iframe for exactly
// one. One function because the template and lastPrintedTab below must agree on it.
function showsTabBlock(item: AccordionItem): boolean {
  return printTabs.value.includes((item.value || '').toString()) || singleTab === item.value
}

/*
 * The LAST block that is actually printed — the one that must not carry a trailing margin.
 *
 * ⚑ Decided here and not in css. It used to be `print:last:[&>*:last-child]:mb-0`, which asks
 * whether the block is the last ELEMENT of its parent — and it never is: `Notifications.vue` (base)
 * renders an empty `<span class="hidden">` behind us, so the rule could not fire in a print run,
 * which is the only run it was written for. The trailing 48px (print:mb-12) then pushed a page whose
 * content ended anywhere above 878px onto a second, empty sheet.
 *
 * Which block is last is OUR knowledge, not the dom's. Asking css about siblings that belong to
 * another component makes our page count depend on that component's markup.
 */
const lastPrintedTab = computed((): string => {
  const printed: AccordionItem[] = accordionItems.value.filter(item => showsTabBlock(item))
  return (printed[printed.length - 1]?.value || '').toString()
})

// Combine tabs as accordion items
const accordionItems = computed((): AccordionItem[] => {
  const res: AccordionItem[] = []
  const tabs: TabsItem[] = detailTabs.value
  const tabs2: { [value: string]: TabsItem[] } = detailTabs2.value
  for (const tab of tabs) {
    const tabName: string = (tab.value || '').toString()
    const subTabs: TabsItem[] = (tabName && tabs2[tabName]) || []
    // The one flattened tab: ONE entry carrying the tab's label over the subtab's value — the
    // display loses the level, the value keeps it (ut_wilo_isFlattenedTab).
    if (ut_wilo_isFlattenedTab(tabName, subTabs.map(sub => (sub.value || '').toString()))) {
      res.push({ label: tab.label, value: tabName + '|' + ((subTabs[0]?.value || '').toString()) })
    }
    else if (subTabs.length) {
      for (const tab2 of subTabs) {
        res.push({ label: tab2.label, value: (tabName + '|' + (tab2.value) || '').toString() })
      }
    }
    else {
      res.push({ label: tab.label, value: (tab.value || '').toString() })
    }
  }
  return res
})

// For mobile accordion view special routine for routing
function changeAccordion(): void {
  const split: string[] = (selectedAccordion.value || '').split('|')
  const tab: string = split[0] || ''
  const tab2: string = split.length > 1 ? split[1] || '' : ''
  ut_navigateNewRoute('', { tab, tab2 }, '', false)
}

// Check if BIM data exists, either template with mediamid props or sales_channel "BIM"
function bimDataExists(): boolean {
  let res: boolean = false
  if (cp.detailData.value) {
    const bimTab: pf.TPfNode | undefined = ut_getObjectByPath(cp.detailData.value, ETab.productBimCad)
    if (bimTab) {
      if (bimTab.nodes?.length) {
        res = true
      }
      else {
        const channelValue: string = ut_getValuesByPath(bimTab, '8ae2819c63a4b2730163a5eb64495cc2', true, -1, '|') // sales_channel
        if (channelValue.includes('BIM')) {
          res = true
        }
      }
    }
  }
  return res
}

// Item state NEW, DISCONTINUED, ...
const itemState = computed((): tpWilo.IItemState => {
  let res: tpWilo.IItemState = {
    state: EItemState.st_none,
    date: '',
  }
  if (cp.detailData.value) {
    const path: string = p.isSeries ? ETab.rangeDefault + '/' + ELogicGroup.rangeDefaultAdditional : ETab.productDefault + '/' + ELogicGroup.productAdditional
    const salesAvailability: Date = new Date(ut_getValuesByPath(cp.detailData.value, path + '/sales_availability_date') || '') // sales_availability
    const salesDiscontinuation: Date = new Date(ut_getValuesByPath(cp.detailData.value, path + '/sales_discontinuation_date') || '') // sales_discontinuation
    const salesIntroduction: Date = new Date(ut_getValuesByPath(cp.detailData.value, path + '/sales_launch_date') || '') // sales_introduction
    const salesExpiration: Date = new Date(ut_getValuesByPath(cp.detailData.value, path + '/sales_expiration_date') || '') // sales_expiration_date
    res = ut_wilo_ItemState(salesAvailability, salesDiscontinuation, salesIntroduction, salesExpiration)
  }

  return res
})

// Wholesaler or shopping links
const shoppingLinks = computed((): tpWilo.IShoppingLink[] => {
  return cp.detailData.value ? ut_wilo_shoppingLinks(cp.detailData.value, p.realCountry) : []
})

// Sales data
const salesData = computed((): pf.TPfNode[] | undefined => {
  let res: pf.TPfNode[] | undefined = undefined
  if (cp.detailData.value) {
    const comp: pf.TPfNode | undefined = ut_getObjectByPath(cp.detailData.value, [ETab.productTechnical, ESubTab.productSalesdata].join('/'))
    if (comp && comp.nodes && comp.nodes[0] && comp.nodes[0].nodes) {
      res = comp.nodes[0].nodes
    }
  }
  return res
})

function salesValue(name: string, combine: boolean, takeValue: boolean) {
  return cp.detailData.value && salesData.value ? ut_wilo_salesValue(salesData.value, name, combine, takeValue, p.countryLanguage, cp.detailData.value.desc, cp.detailData.value.attrs?.id_product || '') : ''
}

/*
 * The serial number, taken out of the typekey sheet the pass carries anyway.
 *
 * Read from the tree by path rather than handed down from Dpp.vue: the sheet is where the number
 * demonstrably IS, so nothing has to be threaded through <Detail> for a single line of head text,
 * and the label travels with it — 'Serial number' is the property's own desc in the reader's
 * language, not a string this component would have to invent and translate.
 *
 * The path is the ordinary answer's: the typekey sub-tab under Technical data, then the sheet and
 * its pump-data node. Only that case matters — when the article number finds no product there is
 * no product head to put anything in.
 *
 * ⚑ fn_getNodesByPath walks level by level and matches attrs.PSM or nodeName, one segment per
 * level. It skips nothing, so the path has to name every step; the last three have no PSM and go
 * by nodeName.
 */
const serialNumber = computed((): string => {
  const detail = cp.detailData.value
  if (!detail) { return '' }
  const path: string = [
    ETab.productTechnical,
    ESubTab.productTypekey,
    '89e1809ca031914001a048c915de3f32', // typekey_general
    '89e1809ca031914001a048cb977e7e76', // Pump_data
    '89e1809ca031914001a048b9693d385f', // SAP_Sernr_SFE
  ].join('/')
  const value: string = ut_getValuesByPath(detail, path)
  // Same shape as the numbers it stands in for (salesValue with combine): label, colon, value.
  return value ? ut_getValuesByPath(detail, path, false) + ': ' + value : ''
})

const priceInfoExists = computed((): boolean => {
  return !!(salesValue('8ae2819c6f7f32a8016f80ced2b12cba', false, true)
    || salesValue('8ae2819c5c81d775015c87d6b02e4d71', false, true)
    || salesValue('8ae2819c6f7f32a8016f80d601a85842', false, true))
})

const photoProdExists = computed((): boolean => {
  return cp.detailData.value ? !!ut_getObjectByPath(cp.detailData.value, [ETab.productDefault, ELogicGroup.productPhotoGraphics].join('/')) : false
})
</script>

<template>
  <template v-if="detailSetup() && cp.detailData.value">
    <!-- last:mb-0 for the case where no tab block follows (series/product info only) — see below -->
    <UContainer v-if="(!ut_isPrint() && !singleTab && !wholeSalerIFrame) || (ut_isPrint() && (printTabs.includes('series-info') || printTabs.includes('product-info')))" class="print:mb-12 print:last:mb-0">

      <!-- *********************************** -->
      <!-- Series -->
      <!-- *********************************** -->
      <!-- Price list: its own header, not the standard one minus a few parts.
           Until 31.08.2026 this WAS the standard block with `!ut_wilo_isPriceList()` gates on
           whatever was unwanted. That holds as long as the difference is only what is left out; the
           moment the layout differs too — title over the full width, photo and text side by side —
           subtracting from a two-column grid means fighting it, so the price list gets its own
           block and the standard one is not rendered at all.

           The two texts on the right are construction type and type key. They come from the
           range_description tab, which a price list no longer prints (it is out of
           def_priceListTabs), so this is the only place they still appear. -->
      <template v-if="isSeries && ut_wilo_isPriceList()">
        <h2 class="font-bold" v-html="cp.detailData.value.desc" />
        <!-- DCI_product_design_SFE under the name, as in the standard header, one step smaller (css-lg) (25.09.2026, Christoph) -->
        <h2
          v-if="ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultTexts, '8ae2819c89cdcef00189e3827dff2b12'].join('/'))"
          class="css-lg"
          v-html="ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultTexts, '8ae2819c89cdcef00189e3827dff2b12'].join('/'))"
        />
        <div class="grid grid-cols-2 gap-8 items-start mb-8 mt-4">
          <!-- align-left: the photo shares the row with the texts, so it belongs against the left
               edge of its column rather than centred in it. no-caption: the price list prints the photo
               without its caption (25.09.2026, Christoph). -->
          <LazyMultiGraphicCarousel
            :node="ut_getObjectByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultGraphics].join('/'))"
            :detail-node="cp.detailData.value"
            :is-old="isOld"
            :no-lazy="true"
            :align-left="true"
            :no-caption="true"
          />
          <!-- One under the other: the column is already half the page, splitting it again would
               leave two hands of text nobody can follow.
               Print: text and headings in the table font (text-table-print, 0.6rem), like the price tables below
               (25.09.2026, Christoph). -->
          <div class="print:text-table-print print:**:text-table-print">
            <div
              v-for="(part, pindex) in ut_wilo_seriesDescriptionParts(ut_getObjectByPath(cp.detailData.value, ETab.rangeDescription), 'h5')"
              :key="pindex"
              class="break-inside-avoid css-description mb-4 print:mb-3"
              v-html="part"
            />
          </div>
        </div>
      </template>

      <template v-else-if="isSeries">
        <div class="grid lg:grid-cols-2 print:grid-cols-2 lg:gap-16 xl:gap-24 mb-8">
          <div class="order-last mt-4 lg:order-first lg:mt-0 print:order-first print:mt-0">
            <!-- Item State -->
            <div
              v-if="itemState.state !== EItemState.st_none"
              class="css-xs rounded-lg py-1 px-2 font-bold inline-block text-white"
              :class="ut_wilo_ItemStateColor(itemState.state)"
              v-html="indexStore.state.paramData.namePath.includes('8ae2819c866cac2e01867ed09cac66fe') ? indexStore.state.paramData.lcda === 'de' ? 'Zurzeit nicht verfügbar' : 'Currently not available'
                : ut_getText('txt_eyecatcher-' + itemState.state) + itemState.date"
            />

            <h1 v-html="cp.detailData.value.desc" />
            <!-- DCI_product_design_SFE -->
            <h2
              v-if="ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultTexts, '8ae2819c89cdcef00189e3827dff2b12'].join('/'))"
              class="mb-6"
              v-html="ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultTexts, '8ae2819c89cdcef00189e3827dff2b12'].join('/'))"
            />
            <!-- desc_promotional_subline_SFE — hidden in EmpCo markets for now (isEmpCo) -->
            <h3
              v-if="!isEmpCo && ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultTexts, '8ae2819c6314804e0163214987566f88'].join('/'))"
              class="mb-4"
              v-html="ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultTexts, '8ae2819c6314804e0163214987566f88'].join('/'))"
            />
            <!-- desc_7_advantages_SFE — hidden in EmpCo markets for now (isEmpCo) -->
            <div
              v-if="!isEmpCo && ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultTexts, '8ae2819d5b31c2ca015b3dc499c9451c'].join('/'))"
              class="css-list"
              v-html="ut_getValuesByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultTexts, '8ae2819d5b31c2ca015b3dc499c9451c'].join('/'))"
            />
            <!-- TEMPORARY: in EmpCo markets the construction type (range_description, cod Design) stands
                 in for the two texts above — without the "Bauart" heading, in the size of the subline (h3 → css-xl). -->
            <template v-if="isEmpCo">
              <div
                v-for="(part, pindex) in ut_wilo_seriesDesignParts(ut_getObjectByPath(cp.detailData.value, ETab.rangeDescription))"
                :key="'empco' + pindex"
                class="css-description css-xl mb-4"
                v-html="part"
              />
            </template>
          </div>
          <div>
            <template v-if="!ut_isPrint()">
              <div v-if="!isDpp && !ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])" class="hidden lg:flex lg:justify-end -mr-2 flex-wrap gap-y-2 gap-x-4">
                <!-- CartIcons Desktop -->
                <LazyCartIcons
                  :item="cp.detailData.value"
                  :config="cp.indexStore.getCurrentConfig"
                  :name-path="cp.indexStore.state.currentNode?.namePath || ''"
                  :size="EIconSize.item"
                  :is-product="!isSeries"
                  :is-list="false"
                  :is-internal="isInternal"
                  :is-old="isOld"
                  :real-country="realCountry"
                  :show-pdf="true"
                  @update:show-modal-pdf="showModalPdf = $event"
                />
                <LazySettingsSmall v-if="!isDpp" :is-historic="isHistoric" :real-country="realCountry" :is-internal="isInternal" :show-frequency="indexStore.getIsNavigation || (indexStore.getIsConfig && !isSeries)" />
              </div>

              <!-- Settings mobile -->
              <div v-if="ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])" class="lg:hidden flex justify-end -mr-2">
                <LazySettingsSmall v-if="!isDpp" :is-historic="isHistoric" :real-country="realCountry" :is-internal="isInternal" :show-frequency="indexStore.getIsNavigation || (indexStore.getIsConfig && !isSeries)" />
              </div>
            </template>

            <!-- Photo Carousel -->
            <LazyMultiGraphicCarousel
              :node="ut_getObjectByPath(cp.detailData.value, [ETab.rangeDefault, ELogicGroup.rangeDefaultGraphics].join('/'))"
              :detail-node="cp.detailData.value"
              :is-old="isOld"
              :no-lazy="true"
            />
          </div>
        </div>

        <!-- Shopping link & Cart Icons -->
        <div v-if="!ut_isPrint()" class="flex flex-wrap gap-x-6 gap-y-4">
          <LazyLinkTeaser v-if="indexStore.state.paramData.rcco === 'us'" :rcco="indexStore.state.paramData.rcco" />
          <!-- Shopping link -->
          <LazyShoppingLinks
            v-if="shoppingLinks.length > 0"
            :shopping-links="shoppingLinks"
            :prod-desc="cp.detailData.value.desc"
            :article-no-text="[
              salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', true, true),
              salesValue('8ae2819c6434e473016437946adf01a1', true, true),
              salesValue('8ae2819c63a4b2730163a67c48a17325', true, true),
              salesValue('8ae2819c63a4b2730163a67c0a852fb3', true, true),
              salesValue('8ae2819c6434e47301643794cd913e88', true, true),
            ]"
            :photo="ut_getObjectByPath(cp.detailData.value, isSeries ? [ETab.rangeDefault, ELogicGroup.rangeDefaultGraphics].join('/') : [ETab.productDefault, ELogicGroup.productPhotoGraphics].join('/'))"
            :real-country="realCountry"
          />
          <div v-if="!isDpp && ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])" class="-ml-2 flex lg:hidden flex-wrap gap-y-2 gap-x-4">
            <!-- CartIcons -->
            <LazyCartIcons
              :item="cp.detailData.value"
              :config="cp.indexStore.getCurrentConfig"
              :name-path="cp.indexStore.state.currentNode?.namePath || ''"
              :size="EIconSize.item"
              :is-product="!isSeries"
              :is-list="false"
              :is-internal="isInternal"
              :is-old="isOld"
              :real-country="realCountry"
              :show-pdf="true"
              @update:show-modal-pdf="showModalPdf = $event"
            />
          </div>
        </div>
      </template>

      <!-- *********************************** -->
      <!-- Product -->
      <!-- *********************************** -->
      <template v-else>
        <!-- Settings mobile -->
        <div v-if="!ut_isPrint() && ut_isScreenSize([EScreenSize.sc_xs])" class="sm:hidden flex justify-end -mr-2">
          <LazySettingsSmall v-if="!isDpp" :is-historic="isHistoric" :real-country="realCountry" :is-internal="isInternal" :show-frequency="indexStore.getIsNavigation || (indexStore.getIsConfig && !isSeries)" />
        </div>
        <div class="grid grid-cols-6 gap-x-8 gap-y-8">
          <div v-if="photoProdExists" class="flex col-span-6 sm:col-span-3 2xl:col-span-1 print:col-span-2">
            <LazyMultiGraphicCarousel
              :node="ut_getObjectByPath(cp.detailData.value, [ETab.productDefault, ELogicGroup.productPhotoGraphics].join('/'))"
              :detail-node="cp.detailData.value"
              :is-product="true"
              :is-old="isOld"
              :no-lazy="true"
            />
          </div>

          <div v-if="!ut_isPrint()" class="col-span-6 sm:col-span-3 flex 2xl:col-span-2 order-last sm:order-0 2xl:order-last ">
            <!-- Settings -->
            <div class="mr-auto md:mr-0 md:ml-auto">
              <div v-if="!ut_isScreenSize([EScreenSize.sc_xs])" class="hidden sm:flex justify-end -mr-2 mb-2">
                <LazySettingsSmall v-if="!isDpp" :is-historic="isHistoric" :real-country="realCountry" :is-internal="isInternal" :show-frequency="indexStore.getIsNavigation || (indexStore.getIsConfig && !isSeries)" />
              </div>
              <div v-if="!isOld && priceInfoExists">
                <div class="bg-gunmetal text-white rounded-lg p-6">
                  <div
                    v-if="salesValue('8ae2819c6f7f32a8016f80ced2b12cba', false, true)"
                    :class="shoppingLinks.length > 0 || salesValue('8ae2819c5c81d775015c87d6b02e4d71', true, true) || salesValue('8ae2819c6f7f32a8016f80d601a85842', true, true) ? 'mb-6' : ''"
                  >
                    <h4 class="text-white" v-html="salesValue('8ae2819c6f7f32a8016f80ced2b12cba', false, true)" /> <!-- price -->
                    <div v-html="salesValue('8ae2819c6f7f32a8016f80ced2b12cba', false, false)" /> <!-- price -->
                  </div>
                  <div
                    v-if="salesValue('8ae2819c5c81d775015c87d6b02e4d71', true, true) || salesValue('8ae2819c6f7f32a8016f80d601a85842', true, true)"
                    :class="shoppingLinks.length > 0 ? 'mb-6' : ''"
                  >
                    <div v-html="salesValue('8ae2819c5c81d775015c87d6b02e4d71', true, true)" /> <!-- price group -->
                    <div v-html="salesValue('8ae2819c6f7f32a8016f80d601a85842', true, true)" /> <!-- delivery time -->
                  </div>

                  <!-- Shopping link -->
                  <div v-if="shoppingLinks.length > 0">
                    <LazyShoppingLinks
                      :shopping-links="shoppingLinks"
                      :prod-desc="cp.detailData.value.desc"
                      :article-no-text="[
                        salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', true, true),
                        salesValue('8ae2819c6434e473016437946adf01a1', true, true),
                        salesValue('8ae2819c63a4b2730163a67c48a17325', true, true),
                        salesValue('8ae2819c63a4b2730163a67c0a852fb3', true, true),
                        salesValue('8ae2819c6434e47301643794cd913e88', true, true),
                      ]"
                      :photo="ut_getObjectByPath(cp.detailData.value, isSeries ? [ETab.rangeDefault, ELogicGroup.rangeDefaultGraphics].join('/') : [ETab.productDefault, ELogicGroup.productPhotoGraphics].join('/'))"
                      :real-country="realCountry"
                      :invert-button="true"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div :class="photoProdExists ? 'col-span-6 2xl:col-span-3 print:col-span-4' : 'col-span-7 2xl:col-span-4 print:col-span-5'">
            <div>
              <!-- Item State -->
              <div
                v-if="itemState.state !== EItemState.st_none"
                class="css-xs rounded-lg py-1 px-2 font-bold inline-block text-white"
                :class="ut_wilo_ItemStateColor(itemState.state)"
                v-html="indexStore.state.paramData.namePath.includes('8ae2819c866cac2e01867ed09cac66fe') ? indexStore.state.paramData.lcda === 'de' ? 'Zurzeit nicht verfügbar' : 'Currently not available'
                  : ut_getText('txt_eyecatcher-' + itemState.state) + itemState.date"
              />
              <!-- Headlines -->
              <h1 v-html="cp.detailData.value.desc" />

              <h2
                v-if="ut_getValuesByPath(cp.detailData.value, [ETab.productDefault, ETab.productAdditional, '8ae2819c9993428b01999ff325396f83'].join('/'))"
                class="mb-6 print:mb-4"
                v-html="ut_getValuesByPath(cp.detailData.value, [ETab.productDefault, ETab.productAdditional, '8ae2819c9993428b01999ff325396f83'].join('/'))"
              />  <!-- DCI_product_design_type_SFE -->
              <h2
                v-else-if="ut_getValuesByPath(cp.detailData.value, [ETab.productDefault, ETab.productAdditional, '89e1809c9b9905b8019ba35f76283ec5'].join('/'))"
                class="mb-6 print:mb-4"
                v-html="ut_getValuesByPath(cp.detailData.value, [ETab.productDefault, ETab.productAdditional, '89e1809c9b9905b8019ba35f76283ec5'].join('/'))"
              />  <!-- DCI_product_design_accessory_type_SFE -->

              <!-- Product sales data -->
              <div v-if="!isDpp && salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', false, true)" class="font-bold">
                <!-- articleNo -->
                <div v-if="ut_isPrint()">
                  <b v-html="salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', false, false) + ':'" />
                  <a
                    :href="'https://wilo.com/quick/' + countryLanguage + '/articleNumber/' + salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', false, true)"
                    class="text-left text-primary underline pl-1"
                    v-html="salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', false, true)"
                  />
                </div>
                <div
                  v-else
                  v-html="salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', true, true)"
                />

                <div v-html="salesValue('8ae2819c6434e473016437946adf01a1', true, true)" /> <!-- LVI -->
                <div v-html="salesValue('8ae2819c63a4b2730163a67c48a17325', true, true)" /> <!-- NRF -->
                <div v-html="salesValue('8ae2819c63a4b2730163a67c0a852fb3', true, true)" /> <!-- RSK -->
                <div v-html="salesValue('8ae2819c6434e47301643794cd913e88', true, true)" /> <!-- VVS -->
              </div>

              <!--
                In the pass only the WHOLESALER numbers fall away: LVI/NRF/RSK/VVS answer "how do I
                order this one", and that is not what a serial number asked.
                The article number stays — it names the product the serial number resolved to, and
                without it the pass says what the thing is but never which article it is. It sits
                ABOVE the serial number, so the head reads "which product" before "the number you
                typed", which is also the order the reader arrived in.
                ⚑ Narrowed on 08.09.2026: the 07.09. version dropped the article number together
                with the wholesaler numbers, which took one too many with it.
              -->
              <div v-else-if="isDpp && (salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', false, true) || serialNumber)" class="font-bold">
                <!-- articleNo -->
                <div v-if="salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', false, true)" v-html="salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', true, true)" />
                <div v-if="serialNumber" v-html="serialNumber" />
              </div>
            </div>
            <div v-if="!ut_isPrint()" class="mt-8 flex flex-wrap gap-y-4 gap-x-6">
              <template v-if="!isOld ">
                <LazyLinkTeaser v-if="indexStore.state.paramData.rcco === 'us'" :rcco="indexStore.state.paramData.rcco" />
                <div v-if="!priceInfoExists && shoppingLinks.length > 0">
                  <LazyShoppingLinks
                    :shopping-links="shoppingLinks"
                    :prod-desc="cp.detailData.value.desc"
                    :article-no-text="[
                      salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', true, true),
                      salesValue('8ae2819c6434e473016437946adf01a1', true, true),
                      salesValue('8ae2819c63a4b2730163a67c48a17325', true, true),
                      salesValue('8ae2819c63a4b2730163a67c0a852fb3', true, true),
                      salesValue('8ae2819c6434e47301643794cd913e88', true, true),
                    ]"
                    :photo="ut_getObjectByPath(cp.detailData.value, isSeries ? [ETab.rangeDefault, ELogicGroup.rangeDefaultGraphics].join('/') : [ETab.productDefault, ELogicGroup.productPhotoGraphics].join('/'))"
                    :real-country="realCountry"
                    :invert-button="false"
                  />
                </div>
              </template>

              <!-- CartIcons -->
              <div v-if="!isDpp" class="flex flex-wrap gap-y-2 gap-x-4 -mx-2">
                <CartIcons
                  :item="cp.detailData.value"
                  :config="cp.indexStore.getCurrentConfig"
                  :name-path="cp.indexStore.state.currentNode?.namePath || ''"
                  :size="EIconSize.item"
                  :is-product="!isSeries"
                  :is-list="false"
                  :is-internal="isInternal"
                  :is-old="isOld"
                  :real-country="realCountry"
                  :show-pdf="true"
                  @update:show-modal-pdf="showModalPdf = $event"
                />
                <!-- Link to series -->
                <UButton
                  v-if="!(isInvisibleOem || isMotors)"
                  variant="shadow"
                  color="tertiary"
                  icon="i-wilo-square-arrow-up-right-solid-full"
                  :label="ut_getText('label_catdesc') + ' ' + seriesItem?.label"
                  :to="seriesItem?.to"
                  @click="cpBreadCrumb.deleteScrollPos()"
                />
              </div>
            </div>
          </diV>
        </div>
      </template>
    </UContainer>

    <!-- *********************************** -->
    <!-- Tabs -->
    <!-- *********************************** -->
    <template v-if="detailTabs.length && cp.detailData.value.nodes">
      <div v-if="!ut_isPrint() && !singleTab && !wholeSalerIFrame" class="mt-12 md:mt-24">
        <!-- Desktop view -->
        <div v-if="ut_isScreenSize([EScreenSize.sc_lg, EScreenSize.sc_none])" class="hidden lg:block relative">
          <div class="hidden xl:block absolute top-[50px] left-0 w-full h-[6px] bg-gunmetal-4" />
          <UContainer>
            <UTabs
              v-model="cp.selectedTab.value"
              :items="detailTabs"
              :unmount-on-hide="false"
              :color="!isOld ? 'primary' : 'tertiary'"
              @update:model-value="cp.changeTab()"
            >
              <template #default="{ item }">
                <span v-html="item.label" />
              </template>
              <template #content="{ item }">
                <template
                  v-for="node in cp.detailData.value.nodes"
                  :key="node.nodeName"
                >
                  <div v-if="item.value === cp.tabName(node)" class="mt-16">

                    <!-- Sub Tabs -->
                    <template v-if="showsSubTabs((item.value || '').toString())">
                      <UTabs
                        v-model="cp.selectedTab2.value"
                        :items="detailTabs2[item.value]"
                        :unmount-on-hide="false"
                        size="sm"
                        orientation="vertical"
                        :color="!isOld ? 'primary' : 'tertiary'"
                        @update:model-value="cp.changeTab2()"
                      >
                        <!-- eslint-disable-next-line vue/no-template-shadow -->
                        <template #content="{ item }">
                          <h3 v-if="!ut_isPrint()" class="mb-8" v-html="item.label" />
                          <LazyDetailContent
                            :node="node.nodes?.find(n => cp.tabName(n) === item.value)"
                            :detail-node="cp.detailData.value"
                            :acc-props="accProps"
                            :country-language="countryLanguage"
                            :real-country="realCountry"
                            :is-internal="isInternal"
                            :article-no="cp.detailData.value.attrs?.id_product || ''"
                            :is-historic="isHistoric"
                            :is-series="isSeries"
                            :is-old="isOld"
                            :is-services="isServices"
                            :is-invisible-oem="isInvisibleOem"
                            :is-motors="isMotors"
                          />
                        </template>
                      </UTabs>
                    </template>

                    <template v-else>
                      <template v-if="item.value === 'range_productlist'">
                        <LazyMask
                          :country-language="countryLanguage"
                          :real-country="realCountry"
                          :is-historic="isHistoric"
                          :is-series="isSeries"
                          :modify-date="isSeries && itemState.state === EItemState.st_expired"
                        />
                        <hr class="my-8">
                        <LazyList
                          :country-language="countryLanguage"
                          :real-country="realCountry"
                          :modify-date="isSeries && itemState.state === EItemState.st_expired"
                          :is-old="isOld"
                          :is-internal="isInternal"
                        />
                      </template>
                      <template v-else>
                        <LazyDetailContent
                          :node="node"
                          :detail-node="cp.detailData.value"
                          :acc-props="accProps"
                          :country-language="countryLanguage"
                          :real-country="realCountry"
                          :is-internal="isInternal"
                          :article-no="cp.detailData.value.attrs?.id_product || ''"
                          :is-historic="isHistoric"
                          :is-series="isSeries"
                          :is-old="isOld"
                          :is-services="isServices"
                          :is-invisible-oem="isInvisibleOem"
                          :is-motors="isMotors"
                        />
                      </template>
                    </template>
                  </div>
                </template>
              </template>
            </UTabs>
          </UContainer>
        </div>

        <!-- Mobile view -->
        <div v-if="ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])" class="block lg:hidden">
          <UContainer>
            <UAccordion
              v-model="selectedAccordion"
              trailing-icon="i-wilo-xmark-solid-full"
              :items="accordionItems"
              :unmount-on-hide="false"
              :ui="isOld ? {
                trigger: 'focus-visible:outline-tertiary',
                trailingIcon: 'group-data-[state=open]:text-tertiary',
                label: 'group-data-[state=open]:text-tertiary',
              } : {}"
              @update:model-value="changeAccordion()"
            >
              <template #default="{ item }">
                <span v-html="item.label" />
              </template>
              <template #content="{ item }">
                <LazyDetailAccordionPrint
                  :nodes="cp.detailData.value.nodes"
                  :detail-node="cp.detailData.value"
                  :acc-props="accProps"
                  :item-value="item.value || ''"
                  :country-language="countryLanguage"
                  :real-country="realCountry"
                  :article-no="cp.detailData.value.attrs?.id_product || ''"
                  :is-historic="isHistoric"
                  :is-series="isSeries"
                  :is-internal="isInternal"
                  :is-old="isOld"
                  :is-services="isServices"
                  :is-invisible-oem="isInvisibleOem"
                  :is-motors="isMotors"
                />
              </template>
            </UAccordion>
          </UContainer>
        </div>
      </div>

      <!-- Print/IFrame tabs -->
      <template v-for="item in accordionItems" v-else-if="ut_isPrint() || singleTab" :key="item.value">
        <!-- Trailing bottom margin on the very last block makes Chrome emit an extra blank page in
             the PDF, so the last PRINTED block loses it (the print:mb-12 spacers) — which block that
             is comes from lastPrintedTab, not from a css :last-child. -->
        <div v-if="showsTabBlock(item)" :class="[singleTab ? 'p-4' : '', item.value === lastPrintedTab ? 'print:[&>*:last-child]:mb-0' : '']">
          <LazyDetailAccordionPrint
            :nodes="cp.detailData.value.nodes"
            :detail-node="cp.detailData.value"
            :acc-props="accProps"
            :item-value="item.value || ''"
            :country-language="countryLanguage"
            :real-country="realCountry"
            :is-internal="isInternal"
            :article-no="cp.detailData.value.attrs?.id_product || ''"
            :is-historic="isHistoric"
            :is-series="isSeries"
            :is-old="isOld"
            :is-services="isServices"
            :is-invisible-oem="isInvisibleOem"
            :is-motors="isMotors"
          />
        </div>
      </template>
      <!-- Price list pdf: one note after every object (Christoph, 25.09.2026) — each object is rendered as its
           own page and merged afterwards, so the end of this page is the end of the object. Always, with or
           without accessories. Only a top margin: a trailing bottom margin makes Chrome add a blank page. In the table font (25.09.2026). -->
      <p
        v-if="ut_isPrint() && ut_wilo_isPriceList()"
        class="mt-8 text-left text-table-print"
        v-html="ut_getText('txt_pdf-all-accessories-online')"
      />
    </template>

    <!-- Shopping links iFrame -->
    <div v-if="shoppingLinks.length > 0 && wholeSalerIFrame" class="p-4">
      <LazyShoppingLinksModal
        :shopping-links="shoppingLinks"
        :prod-desc="cp.detailData.value.desc"
        :article-no-text="[
          salesValue('8ae2819d5b31c2ca015b3dc4ba4f1612', true, true),
          salesValue('8ae2819c6434e473016437946adf01a1', true, true),
          salesValue('8ae2819c63a4b2730163a67c48a17325', true, true),
          salesValue('8ae2819c63a4b2730163a67c0a852fb3', true, true),
          salesValue('8ae2819c6434e47301643794cd913e88', true, true),
        ]"
        :photo="ut_getObjectByPath(cp.detailData.value, isSeries ? [ETab.rangeDefault, ELogicGroup.rangeDefaultGraphics].join('/') : [ETab.productDefault, ELogicGroup.productPhotoGraphics].join('/'))"
        :real-country="realCountry"
      />
    </div>

    <!-- PDF Modal -->
    <LazyModalPDF
      v-if="showModalPdf"
      :modal-open="showModalPdf"
      :detail-data="cp.detailData.value"
      :detail-slug="indexStore.state.currentNode.meta?.slug || ''"
      :is-historic="isHistoric"
      :is-motors="isMotors"
      :is-invisible-oem="isInvisibleOem"
      :real-country="realCountry"
      :test-mode="false"
      :is-internal="isInternal"
      @update:close="showModalPdf = false"
    />
  </template>
</template>
